import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useDataContext } from "@/context/DataContext";

interface LegalPageProps {
  defaultTab?: "privacy" | "terms";
}

export default function LegalPage({ defaultTab }: LegalPageProps = {}) {
  const { t, i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "terms"
    ? "terms"
    : searchParams.get("tab") === "privacy"
    ? "privacy"
    : (defaultTab || "privacy");
  const [activeTab, setActiveTab] = useState<"privacy" | "terms">(initialTab);
  const { clinicInfo, settingsLoading } = useDataContext();

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "terms" || tabParam === "privacy") {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: "privacy" | "terms") => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const rawPrivacySections = clinicInfo?.privacySections || [];
  const rawTermsSections = clinicInfo?.termsSections || [];

  const effectivePrivacySections = rawPrivacySections.map((s: any) => ({
    title: isAr ? (s.title || s.title_en || "") : (s.title_en || s.title || ""),
    content: isAr ? (s.content || s.content_en || "") : (s.content_en || s.content || ""),
  }));

  const effectiveTermsSections = rawTermsSections.map((s: any) => ({
    title: isAr ? (s.title || s.title_en || "") : (s.title_en || s.title || ""),
    content: isAr ? (s.content || s.content_en || "") : (s.content_en || s.content || ""),
  }));

  const sections = activeTab === "privacy" ? effectivePrivacySections : effectiveTermsSections;
  const currentSummary = activeTab === "privacy"
    ? (isAr ? clinicInfo?.privacy : (clinicInfo?.privacyEn || clinicInfo?.privacy))
    : (isAr ? clinicInfo?.terms : (clinicInfo?.termsEn || clinicInfo?.terms));

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="min-h-screen bg-brand-cream-50">
      {/* Hero */}
      <section className="relative bg-brand-forest-900 py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <ScrollReveal>
            <div className="inline-flex items-center gap-2 bg-brand-cream-300/15 border border-brand-cream-300/25 text-brand-cream-200 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
              <i className="ri-shield-check-line"></i>
              {isAr ? "الموثوقية والشفافية" : "Trust & Transparency"}
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-brand-cream-100 mb-4 leading-tight">
              {isAr ? "سياسة الخصوصية وشروط الاستخدام" : "Privacy Policy & Terms of Use"}
            </h1>
            <p className="text-brand-cream-200/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              {isAr
                ? "نلتزم بالشفافية الكاملة في كيفية تعاملنا مع بياناتك وحقوقك كمستخدم لمنصتنا."
                : "We are committed to complete transparency in how we handle your data and your rights as a user of our platform."}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-brand-cream-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 py-3">
            <button
              onClick={() => handleTabChange("privacy")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === "privacy"
                  ? "bg-brand-forest-900 text-white shadow-md"
                  : "text-brand-forest-700 hover:bg-brand-cream-100"
              }`}
            >
              <i className="ri-shield-keyhole-line"></i>
              {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
            </button>
            <button
              onClick={() => handleTabChange("terms")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === "terms"
                  ? "bg-brand-forest-900 text-white shadow-md"
                  : "text-brand-forest-700 hover:bg-brand-cream-100"
              }`}
            >
              <i className="ri-file-list-3-line"></i>
              {isAr ? "شروط الاستخدام" : "Terms of Use"}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="mb-8 flex items-center gap-3 text-sm text-brand-forest-500">
          <Link to="/" className="hover:text-brand-forest-900 transition-colors cursor-pointer">
            {isAr ? "الرئيسية" : "Home"}
          </Link>
          <i className={isAr ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}></i>
          <span className="text-brand-forest-900 font-medium">
            {activeTab === "privacy"
              ? isAr ? "سياسة الخصوصية" : "Privacy Policy"
              : isAr ? "شروط الاستخدام" : "Terms of Use"}
          </span>
        </div>

        {settingsLoading && sections.length === 0 ? (
          <div className="space-y-6 animate-pulse">
            <div className="h-24 bg-brand-cream-200/50 rounded-2xl"></div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-brand-cream-200 p-6 md:p-8 space-y-4">
                <div className="h-6 bg-brand-cream-200/70 rounded w-1/3"></div>
                <div className="h-4 bg-brand-cream-100 rounded w-full"></div>
                <div className="h-4 bg-brand-cream-100 rounded w-5/6"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {currentSummary && (
              <div className="mb-8 bg-white border border-brand-cream-200 rounded-2xl p-6 shadow-sm text-brand-forest-900">
                <div className="flex items-start gap-3.5">
                  <div className="w-9 h-9 rounded-xl bg-brand-forest-100 text-brand-forest-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={activeTab === "privacy" ? "ri-shield-check-line text-lg" : "ri-file-text-line text-lg"}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-forest-600 mb-1">
                      {activeTab === "privacy" 
                        ? (isAr ? "نظرة عامة على سياسة الخصوصية" : "Privacy Policy Overview")
                        : (isAr ? "نظرة عامة على شروط الاستخدام" : "Terms of Use Overview")}
                    </p>
                    <p className="text-sm md:text-base leading-relaxed text-gray-700 whitespace-pre-wrap">{currentSummary}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-8">
              {sections.map((section, index) => {
                const cleanTitle = (section.title || "").replace(/^\d+[\.\-\:]\s*/, "");
                return (
                  <ScrollReveal key={`${section.title}-${index}`} delay={index * 0.05}>
                    <div className="bg-white rounded-xl border border-brand-cream-200 p-6 md:p-8 shadow-sm">
                      <h2 className="text-lg md:text-xl font-bold text-brand-forest-900 mb-4 flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-brand-forest-900 text-brand-cream-100 rounded-lg text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        {cleanTitle || section.title}
                      </h2>
                      <p className="text-brand-forest-600 leading-[1.8] text-sm md:text-base whitespace-pre-wrap">
                        {section.content}
                      </p>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </>
        )}

        {/* Last Updated */}
        <div className="mt-12 text-center">
          <p className="text-sm text-brand-forest-400">
            {isAr
              ? `آخر تحديث: أبريل 2026 — ذا مديكال أفينيو`
              : `Last Updated: April 2026 — The Medical Avenue`}
          </p>
        </div>

        {/* Contact CTA */}
        <div className="mt-10 bg-brand-forest-900 rounded-2xl p-8 md:p-10 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-brand-cream-300/15 rounded-full mx-auto mb-4">
            <i className="ri-question-line text-brand-cream-200 text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-brand-cream-100 mb-2">
            {isAr ? "هل لديك استفسار؟" : "Have a Question?"}
          </h3>
          <p className="text-brand-cream-200/60 text-sm mb-6 max-w-md mx-auto">
            {isAr
              ? "فريقنا جاهز للإجابة على أي استفسارات تتعلق بسياسة الخصوصية أو شروط الاستخدام."
              : "Our team is ready to answer any questions regarding our privacy policy or terms of use."}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-brand-cream-300 hover:bg-brand-cream-200 text-brand-forest-900 text-sm font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            {isAr ? "تواصل معنا" : "Contact Us"}
          </Link>
        </div>
      </section>
    </div>
  );
}